import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { AuthError, sendPasswordResetEmail } from 'firebase/auth';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getFirebaseAuth } from '../config/firebase';
import { getAuthErrorMessage } from '../utils/authErrors';

/**
 * Dedicated forgot-password page (SOPHIA-34869 follow-up).
 *
 * Standard pattern: "Forgot password?" on the login page links here; the user
 * enters their email and receives a reset link (which lands on /auth/action).
 *
 * To prevent account enumeration, an unknown email shows the same confirmation
 * as a known one — Firebase with email-enumeration protection behaves the same
 * way, so this also keeps the UX consistent across project configurations.
 */
export default function ForgotPassword(): React.ReactElement {
  const location = useLocation();
  const prefill = (location.state as { email?: string })?.email || '';

  const [email, setEmail] = useState(prefill);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(getFirebaseAuth(), email);
      setSentTo(email);
    } catch (err) {
      const authErr = err as AuthError;
      if (authErr.code === 'auth/user-not-found' || authErr.code === 'auth/user-disabled') {
        // Same confirmation as a known, active account — don't reveal whether an
        // account exists or what state it's in
        setSentTo(email);
      } else if (authErr.code) {
        setError(getAuthErrorMessage(authErr));
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card elevation={0} sx={{ borderRadius: '4px' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/sophia-logo.svg"
                alt="Sophia Learning"
                sx={{ height: 60, mr: 2 }}
              />
              <div>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Sophia Learning Partner Portal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reset your password
                </Typography>
              </div>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {sentTo ? (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  If an account exists for <strong>{sentTo}</strong>, a password reset link is on
                  its way. Please check your inbox.
                </Alert>
                <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                  Back to Sign In
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter the email address for your account and we&apos;ll send you a link to reset
                  your password.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 3 }}
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ mb: 2 }}
                  >
                    {loading ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                </form>

                <Box sx={{ textAlign: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Back to Sign In
                  </Link>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Compliance Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            This system processes student data in compliance with FERPA, COPPA, and SOC 2 Type II
            standards.
            <br />
            Unauthorized access is prohibited.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
